import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  FileText,
  Calculator,
  Monitor,
  BookOpen,
  Lightbulb,
  Shield,
  Globe,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { services } from "@/data";
import { JSX } from "react/jsx-runtime";
import { InView, FadeUp, MotionButton } from "@/lib/animations/components";
import {
  staggerOrchestrator,
  cardItem,
  arrowNudge,
  inViewFadeUp,
} from "@/lib/animations/variants";

const iconMap: Record<string, React.ElementType> = {
  FileText,
  Calculator,
  Monitor,
  BookOpen,
  Lightbulb,
  Shield,
  Globe,
  GraduationCap,
};

export function ServicesSection(): JSX.Element {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <InView className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-[#1A1A1A] mb-4">
            Services
          </h2>
          <p className="text-[#6B6B6B] max-w-xl mx-auto leading-relaxed">
            From registration and compliance to advisory and dispute resolution
            — we cover every aspect of your tax obligations in Kenya.
          </p>
        </InView>

 
        <motion.div
          variants={staggerOrchestrator(0.07, 0.1)}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {services.map((service) => {
            const Icon = iconMap[service.icon] || FileText;
            return (
              <motion.div key={service.id} variants={cardItem}>
                <Link
                  to={`/services/${service.slug}` as never}
                  className="group block h-full bg-[#FAF7F2] hover:bg-[#6B1E2A] rounded-xl p-6 transition-colors duration-300 border-[#E8E0D0] hover:border-transparent"
                >
                  <div className="w-12 h-12 bg-[#6B1E2A]/10 group-hover:bg-white/15 rounded-xl flex items-center justify-center mb-5 transition-colors">
                    <Icon
                      size={22}
                      className="text-[#6B1E2A] group-hover:text-white transition-colors"
                    />
                  </div>

                  <h3 className="font-semibold text-[#1A1A1A] group-hover:text-white mb-2 transition-colors leading-snug">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#6B6B6B] group-hover:text-white/75 leading-relaxed transition-colors line-clamp-2">
                    {service.shortDescription}
                  </p>

      
                  <motion.div
                    variants={arrowNudge}
                    initial="rest"
                    whileHover="hovered"
                    className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#6B1E2A] group-hover:text-[#C9A84C] transition-colors"
                  >
                    Learn More
                    <ArrowRight size={12} />
                  </motion.div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <FadeUp {...inViewFadeUp(0.2)} className="text-center mt-12">
          <MotionButton>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-[#6B1E2A] text-[#6B1E2A] font-semibold rounded-xl hover:bg-[#6B1E2A] hover:text-white transition-all"
            >
              View All Services
              <ArrowRight size={16} />
            </Link>
          </MotionButton>
        </FadeUp>
      </div>
    </section>
  );
}
